import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import Chart from './Chart';
import { Button } from '../Button';

// ExportableVisualization props
export interface ExportableVisualizationProps {
  /**
   * Chart type
   */
  type: 'bar' | 'line' | 'pie' | 'scatter';
  /**
   * Chart data
   */
  data: {
    labels: string[];
    datasets: Array<{
      label: string;
      data: number[];
      backgroundColor?: string | string[];
      borderColor?: string;
      borderWidth?: number;
    }>;
  };
  /**
   * Chart options
   */
  options?: any;
  /**
   * Width of the chart
   */
  width?: number | string;
  /**
   * Height of the chart
   */
  height?: number | string;
  /**
   * Available export formats
   */
  exportFormats?: Array<'png' | 'jpeg' | 'pdf' | 'csv' | 'excel'>;
  /**
   * Additional class name
   */
  className?: string;
}

// Styled components
const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const ChartContainer = styled.div<{ $width: number | string; $height: number | string }>`
  width: ${props => typeof props.$width === 'number' ? `${props.$width}px` : props.$width};
  height: ${props => typeof props.$height === 'number' ? `${props.$height}px` : props.$height};
  position: relative;
`;

const ExportButtonsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 1rem;
  gap: 0.5rem;
`;

const DropdownContainer = styled.div`
  position: relative;
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  z-index: 10;
  min-width: 120px;
  background-color: white;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  margin-top: 0.25rem;
  overflow: hidden;
`;

const DropdownItem = styled.div`
  padding: 0.5rem 1rem;
  cursor: pointer;
  font-size: 0.875rem;
  
  &:hover {
    background-color: ${props => props.theme?.colors?.primary?.[50] || '#E6F7FF'};
  }
`;

/**
 * ExportableVisualization component for charts with export functionality
 */
const ExportableVisualization: React.FC<ExportableVisualizationProps> = ({
  type,
  data,
  options = {},
  width = '100%',
  height = 400,
  exportFormats = ['png', 'jpeg', 'csv'],
  className,
}) => {
  const chartRef = useRef<any>(null);
  const [showExportMenu, setShowExportMenu] = useState(false);
  
  const handleExportClick = () => {
    setShowExportMenu(!showExportMenu);
  };
  
  const exportToImage = (format: 'png' | 'jpeg') => {
    if (!chartRef.current) return;
    
    const chart = chartRef.current;
    const link = document.createElement('a');
    link.download = `chart-export.${format}`;
    link.href = chart.toBase64Image(format);
    link.click();
    
    setShowExportMenu(false);
  };
  
  const exportToPDF = () => {
    if (!chartRef.current) return;
    
    // This is a simplified implementation
    // In a real implementation, you would use a library like jsPDF
    const chart = chartRef.current;
    const canvas = chart.canvas;
    const imgData = canvas.toDataURL('image/png');
    
    // Create a simple PDF using window.print()
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    
    printWindow.document.write(`
      <html>
        <head>
          <title>Chart Export</title>
          <style>
            body {
              display: flex;
              justify-content: center;
              align-items: center;
              height: 100vh;
              margin: 0;
            }
            img {
              max-width: 100%;
              max-height: 100%;
            }
            @media print {
              body {
                height: auto;
              }
            }
          </style>
        </head>
        <body>
          <img src="${imgData}" alt="Chart Export" />
          <script>
            window.onload = function() {
              setTimeout(function() {
                window.print();
                window.close();
              }, 500);
            };
          </script>
        </body>
      </html>
    `);
    
    printWindow.document.close();
    setShowExportMenu(false);
  };
  
  const exportToCSV = () => {
    if (!data || !data.labels || !data.datasets) return;
    
    // Create CSV content
    const headers = ['Label', ...data.datasets.map(ds => ds.label || 'Dataset')];
    const rows = data.labels.map((label, i) => {
      return [
        label,
        ...data.datasets.map(ds => ds.data[i])
      ];
    });
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');
    
    // Create and download the file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'chart-data.csv';
    link.click();
    
    setShowExportMenu(false);
  };
  
  const exportToExcel = () => {
    if (!data || !data.labels || !data.datasets) return;
    
    // Create Excel XML content (simplified)
    const headers = ['Label', ...data.datasets.map(ds => ds.label || 'Dataset')];
    const rows = data.labels.map((label, i) => {
      return [
        label,
        ...data.datasets.map(ds => ds.data[i])
      ];
    });
    
    let excelContent = '<?xml version="1.0"?><?mso-application progid="Excel.Sheet"?>';
    excelContent += '<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet" xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">';
    excelContent += '<Worksheet ss:Name="Chart Data"><Table>';
    
    // Add headers
    excelContent += '<Row>';
    headers.forEach(header => {
      excelContent += `<Cell><Data ss:Type="String">${header}</Data></Cell>`;
    });
    excelContent += '</Row>';
    
    // Add data rows
    rows.forEach(row => {
      excelContent += '<Row>';
      row.forEach((cell, i) => {
        const type = i === 0 ? 'String' : 'Number';
        excelContent += `<Cell><Data ss:Type="${type}">${cell}</Data></Cell>`;
      });
      excelContent += '</Row>';
    });
    
    excelContent += '</Table></Worksheet></Workbook>';
    
    // Create and download the file
    const blob = new Blob([excelContent], { type: 'application/vnd.ms-excel' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'chart-data.xls';
    link.click();
    
    setShowExportMenu(false);
  };
  
  const handleExport = (format: 'png' | 'jpeg' | 'pdf' | 'csv' | 'excel') => {
    switch (format) {
      case 'png':
        exportToImage('png');
        break;
      case 'jpeg':
        exportToImage('jpeg');
        break;
      case 'pdf':
        exportToPDF();
        break;
      case 'csv':
        exportToCSV();
        break;
      case 'excel':
        exportToExcel();
        break;
    }
  };
  
  return (
    <Container className={className}>
      <ExportButtonsContainer>
        <DropdownContainer>
          <Button variant="secondary" onClick={handleExportClick}>
            Export
          </Button>
          
          {showExportMenu && (
            <DropdownMenu>
              {exportFormats.map(format => (
                <DropdownItem 
                  key={format}
                  onClick={() => handleExport(format)}
                >
                  Export as {format.toUpperCase()}
                </DropdownItem>
              ))}
            </DropdownMenu>
          )}
        </DropdownContainer>
      </ExportButtonsContainer>
      
      <ChartContainer $width={width} $height={height}>
        <Chart
          type={type}
          data={data}
          options={options}
          ref={chartRef}
        />
      </ChartContainer>
    </Container>
  );
};

export default ExportableVisualization;